import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BondSettingsPanel } from '@peruselab/structure-editor/components';
import { DEFAULT_INK_COLOR, type BondDrawingConfig } from '@peruselab/structure-editor/domain/bonds';
import {
  applicableStyleKeys,
  isStyledAnnotation,
  type AnnotationStyle,
  type AnnotationStyleKey,
  type StyledAnnotation
} from '@peruselab/structure-editor/domain/annotationStyle';
import { ChemicalPalettePicker } from '../ChemicalPalettePicker';
import { Icon } from '../Icon';
import type { UseDrawingDocumentResult } from '../../hooks/useDrawingDocument';
import { selectedCycles, cycleKey } from '@peruselab/structure-editor/domain/cycleFills';
import { appearanceTargets, type MoleculeAppearancePatch } from '@peruselab/structure-editor/domain/moleculeAppearance';
import { BondGlyph } from '../BondGlyph';
import type { StyleDefaultsSyncStatus } from '../../hooks/useStyleDefaultsSync';

// The standard look for new objects (Reset defaults, #862).
const STANDARD_DEFAULTS: AnnotationStyle = { lineStyle: 'solid' };

type StylePatch = Partial<Record<AnnotationStyleKey, AnnotationStyle[AnnotationStyleKey] | undefined>>;

const MIXED = Symbol('mixed');
type Value<T> = T | undefined | typeof MIXED;

const DEFAULT_LINE_WIDTH_PT = 1;
// #866 -- the radius Rounded corners starts at.
const DEFAULT_CORNER_RADIUS_PT = 4;

function shared<K extends AnnotationStyleKey>(targets: readonly StyledAnnotation[], key: K): Value<AnnotationStyle[K]> {
  const accepting = targets.filter((annotation) => applicableStyleKeys(annotation).includes(key));
  if (accepting.length === 0) return undefined;
  const first = accepting[0]!.style?.[key];
  return accepting.every((annotation) => annotation.style?.[key] === first) ? first : MIXED;
}

/** A colour well that previews while the picker is open and commits once it closes (#773 C6). */
function ColorField({
  label,
  value,
  onPreview,
  onCommit,
  onCancel
}: {
  readonly label: string;
  readonly value: string;
  readonly onPreview: (color: string) => void;
  readonly onCommit: () => void;
  readonly onCancel: () => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  const commit = useRef(onCommit);
  useLayoutEffect(() => {
    commit.current = onCommit;
  }, [onCommit]);
  useEffect(() => {
    const input = ref.current;
    if (!input) return;
    // React's onChange is the native `input` event; the native `change` fires when the picker closes.
    const handle = () => commit.current();
    input.addEventListener('change', handle);
    return () => input.removeEventListener('change', handle);
  }, []);
  return (
    <span className="chemical-color-field">
      <input
        ref={ref}
        type="color"
        aria-label={label}
        title={label}
        value={value}
        onChange={(event) => onPreview(event.target.value)}
        onBlur={onCommit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel();
        }}
      />
      <ChemicalPalettePicker
        value={value}
        onSelect={(color) => {
          onPreview(color);
          onCommit();
        }}
      />
    </span>
  );
}

/**
 * #859 (#842 S1, S2, S6; #773 C4-C6) -- Swatches, Fill, Line and Opacity for a set of style targets:
 * the selection (live preview, one undo step per adjustment) or the new-object defaults.
 */
function StyleControls({
  values,
  accepts,
  onPreview,
  onCommit,
  onCancel,
  idPrefix
}: {
  readonly values: { [K in AnnotationStyleKey]: Value<AnnotationStyle[K]> };
  readonly accepts: (key: AnnotationStyleKey) => boolean;
  readonly onPreview: (patch: StylePatch) => void;
  readonly onCommit: () => void;
  readonly onCancel: () => void;
  readonly idPrefix: string;
}) {
  const { t } = useTranslation();
  const [widthText, setWidthText] = useState<string | null>(null);
  const [radiusText, setRadiusText] = useState<string | null>(null);
  const [opacityDraft, setOpacityDraft] = useState<number | null>(null);
  const apply = (patch: StylePatch) => {
    onPreview(patch);
    onCommit();
  };
  const escape = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    setOpacityDraft(null);
    setWidthText(null);
    onCancel();
  };
  const mixed = t('rightRail.mixed');
  const colorValue = values.color === MIXED ? undefined : values.color;
  const fillValue = values.fill === MIXED ? undefined : values.fill;
  const filled = values.fill === MIXED || (fillValue !== undefined && fillValue !== 'none');
  const lineWidth = values.lineWidth === MIXED ? undefined : (values.lineWidth ?? DEFAULT_LINE_WIDTH_PT);
  const opacity = opacityDraft ?? (values.opacity === MIXED ? 100 : Math.round((values.opacity ?? 1) * 100));
  const commitWidth = () => {
    if (widthText === null) return;
    const value = Number(widthText);
    setWidthText(null);
    if (Number.isFinite(value) && value > 0 && value <= 72) apply({ lineWidth: value });
  };
  const commitRadius = () => {
    if (radiusText === null) return;
    const value = Number(radiusText);
    setRadiusText(null);
    if (Number.isFinite(value) && value >= 0 && value <= 72) apply({ cornerRadius: value > 0 ? value : undefined });
  };
  const commitOpacity = () => {
    if (opacityDraft === null) return;
    setOpacityDraft(null);
    onCommit();
  };

  return (
    <div className="style-controls" data-testid={`${idPrefix}-style`}>
      <section className="style-group" aria-labelledby={`${idPrefix}-fill`}>
        <h3 id={`${idPrefix}-fill`}>{t('rightRail.style.fill')}</h3>
        {accepts('fill') ? (
          <div className="style-row">
            <label className="style-check">
              <input
                type="checkbox"
                aria-label={t('rightRail.style.fillOn')}
                checked={filled}
                onChange={(event) =>
                  apply({
                    fill: event.target.checked ? (fillValue && fillValue !== 'none' ? fillValue : '#ffffff') : undefined
                  })
                }
              />
              <span>{values.fill === MIXED ? mixed : t('rightRail.style.fillOn')}</span>
            </label>
            {filled ? (
              <ColorField
                label={t('rightRail.style.fillColor')}
                value={fillValue && fillValue !== 'none' ? fillValue : '#ffffff'}
                onPreview={(color) => onPreview({ fill: color })}
                onCommit={onCommit}
                onCancel={onCancel}
              />
            ) : null}
          </div>
        ) : (
          <p className="rail-hint">{t('rightRail.style.fillNotAvailable')}</p>
        )}
      </section>

      {accepts('background') ? (
        <label className="style-check">
          <input
            type="checkbox"
            aria-label={t('rightRail.style.background')}
            checked={values.background === MIXED || values.background === true}
            onChange={(event) => apply({ background: event.target.checked ? true : undefined })}
          />
          <span>{values.background === MIXED ? mixed : t('rightRail.style.background')}</span>
        </label>
      ) : null}
      <section className="style-group" aria-labelledby={`${idPrefix}-line`}>
        <h3 id={`${idPrefix}-line`}>
          {accepts('lineStyle') || accepts('lineWidth') ? t('rightRail.style.line') : t('rightRail.style.colour')}
        </h3>
        {accepts('outline') ? (
          <label className="style-check">
            <input
              type="checkbox"
              aria-label={t('rightRail.style.outline')}
              checked={values.outline !== false}
              onChange={(event) => apply({ outline: event.target.checked ? undefined : false })}
            />
            <span>{values.outline === MIXED ? mixed : t('rightRail.style.outline')}</span>
          </label>
        ) : null}
        <div className="style-row">
          <span>{values.color === MIXED ? mixed : t('rightRail.lineColor')}</span>
          <ColorField
            label={t('rightRail.lineColor')}
            value={colorValue ?? DEFAULT_INK_COLOR}
            onPreview={(color) => onPreview({ color })}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        </div>
        {accepts('lineStyle') ? (
          <label className="style-row">
            <span>{t('rightRail.lineStyle')}</span>
            <select
              value={values.lineStyle === MIXED ? '' : (values.lineStyle ?? 'solid')}
              onChange={(event) =>
                apply({ lineStyle: event.target.value as NonNullable<AnnotationStyle['lineStyle']> })
              }
            >
              {values.lineStyle === MIXED ? (
                <option value="" disabled>
                  {mixed}
                </option>
              ) : null}
              {(['solid', 'dashed', 'dotted'] as const).map((value) => (
                <option key={value} value={value}>
                  {t(`rightRail.${value}`)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {accepts('lineWidth') ? (
          <label className="style-row">
            <span>{t('rightRail.style.lineWidth')}</span>
            <span className="style-number">
              <input
                type="text"
                inputMode="decimal"
                aria-label={t('rightRail.style.lineWidthPt')}
                placeholder={values.lineWidth === MIXED ? mixed : undefined}
                value={widthText ?? (lineWidth !== undefined ? String(lineWidth) : '')}
                onChange={(event) => setWidthText(event.target.value)}
                onBlur={commitWidth}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') commitWidth();
                  if (event.key === 'Escape') setWidthText(null);
                }}
              />
              <span>{t('rightRail.style.pt')}</span>
            </span>
          </label>
        ) : null}
      </section>

      <section className="style-group" aria-labelledby={`${idPrefix}-opacity`}>
        <h3 id={`${idPrefix}-opacity`}>{t('rightRail.style.opacity')}</h3>
        <div className="style-row">
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            aria-label={t('rightRail.style.opacity')}
            aria-valuetext={values.opacity === MIXED && opacityDraft === null ? mixed : `${opacity}%`}
            value={opacity}
            onChange={(event) => {
              const next = Number(event.target.value);
              setOpacityDraft(next);
              onPreview({ opacity: next >= 100 ? undefined : next / 100 });
            }}
            onPointerUp={commitOpacity}
            onKeyUp={(event) => {
              if (event.key !== 'Escape') commitOpacity();
            }}
            onBlur={commitOpacity}
            onKeyDown={escape}
          />
          <span className="style-value">
            {values.opacity === MIXED && opacityDraft === null ? mixed : `${opacity}%`}
          </span>
        </div>
      </section>
      <section className="style-group" aria-labelledby={`${idPrefix}-effects`}>
        <h3 id={`${idPrefix}-effects`}>{t('rightRail.style.effects')}</h3>
        {accepts('cornerRadius') ? (
          <div className="style-row">
            <label className="style-check">
              <input
                type="checkbox"
                aria-label={t('rightRail.style.rounded')}
                checked={values.cornerRadius === MIXED || (values.cornerRadius ?? 0) > 0}
                onChange={(event) =>
                  apply({ cornerRadius: event.target.checked ? DEFAULT_CORNER_RADIUS_PT : undefined })
                }
              />
              <span>{values.cornerRadius === MIXED ? mixed : t('rightRail.style.rounded')}</span>
            </label>
            {values.cornerRadius !== MIXED && (values.cornerRadius ?? 0) > 0 ? (
              <span className="style-number">
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label={t('rightRail.style.radiusPt')}
                  value={radiusText ?? String(values.cornerRadius)}
                  onChange={(event) => setRadiusText(event.target.value)}
                  onBlur={commitRadius}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') commitRadius();
                    if (event.key === 'Escape') setRadiusText(null);
                  }}
                />
                <span>{t('rightRail.style.pt')}</span>
              </span>
            ) : null}
          </div>
        ) : null}
        {accepts('shadow') ? (
          <label className="style-check">
            <input
              type="checkbox"
              aria-label={t('rightRail.style.shadow')}
              checked={values.shadow === MIXED || values.shadow === true}
              onChange={(event) => apply({ shadow: event.target.checked ? true : undefined })}
            />
            <span>{values.shadow === MIXED ? mixed : t('rightRail.style.shadow')}</span>
          </label>
        ) : null}
        {!accepts('cornerRadius') && !accepts('shadow') ? (
          <p className="rail-hint">{t('rightRail.style.effectsNotAvailable')}</p>
        ) : null}
      </section>
    </div>
  );
}

/**
 * #868 (#842 S7) -- colour, bond width (pt) and atom colours for the selected atoms and bonds: a
 * whole molecule, or just a segment of one (owner, 2026-10-07). Molecules stay fully opaque and
 * never take dashes.
 */
export function MoleculeControls({
  doc,
  control = 'all'
}: {
  readonly doc: UseDrawingDocumentResult;
  readonly control?: 'all' | 'color' | 'width';
}) {
  const { t } = useTranslation();
  const [widthText, setWidthText] = useState<string | null>(null);
  const { atomIds, bondIds } = appearanceTargets(doc.document, doc.selection);
  const cycles = selectedCycles(doc.document, doc.selection);
  const cycleColors = cycles.map(
    (ring) =>
      doc.document.depiction.cycleFills?.find((fill) => cycleKey(fill.atomIds) === cycleKey(ring.atomIds))?.color
  );
  const cycleColor = cycleColors[0];
  const atoms = atomIds.map((id) => doc.document.depiction.atomAppearance?.[id]);
  const bonds = bondIds.map((id) => doc.document.depiction.bondAppearance?.[id]);
  const sharedOf = <T,>(values: readonly (T | undefined)[]): Value<T> =>
    values.length === 0 ? undefined : values.every((value) => value === values[0]) ? values[0] : MIXED;
  const color = sharedOf([...bonds.map((bond) => bond?.color), ...atoms.map((atom) => atom?.color)]);
  const width = sharedOf(bonds.map((bond) => bond?.lineWidth));
  const byElement = sharedOf(atoms.map((atom) => atom?.colorByElement === true));
  const mixed = t('rightRail.style.mixedValue');
  const apply = (patch: MoleculeAppearancePatch) => doc.updateMoleculeStyle(patch);
  const commitWidth = () => {
    if (widthText === null) return;
    const value = Number(widthText);
    setWidthText(null);
    if (Number.isFinite(value) && value > 0 && value <= 72) apply({ lineWidth: value });
  };
  return (
    <div className="style-controls" data-testid="molecule-style">
      {control === 'all' && cycles.length > 0 ? (
        <section className="style-group" aria-label={t('rightRail.style.cycleFill')}>
          <h3>{t('rightRail.style.cycleFill')}</h3>
          <label className="style-row">
            <input
              type="checkbox"
              aria-label={t('rightRail.style.cycleFillOn')}
              checked={cycleColors.some(Boolean)}
              onChange={(event) => apply({ cycleFill: event.target.checked ? '#fdf0cf' : undefined })}
            />
            <span>{t('rightRail.style.cycleFillOn')}</span>
          </label>
          {cycleColors.some(Boolean) ? (
            <ColorField
              label={t('rightRail.style.cycleFillColor')}
              value={cycleColor ?? '#fdf0cf'}
              onPreview={(color) => doc.previewMoleculeStyle({ cycleFill: color })}
              onCommit={doc.commitStylePreview}
              onCancel={doc.cancelStylePreview}
            />
          ) : null}
        </section>
      ) : null}
      {control !== 'width' ? (
        <>
          <div className="style-row">
            <span>{color === MIXED ? mixed : t('rightRail.style.moleculeColour')}</span>
            <ColorField
              label={t('rightRail.style.moleculeColour')}
              value={color === MIXED || color === undefined ? DEFAULT_INK_COLOR : color}
              onPreview={(value) => doc.previewMoleculeStyle({ color: value })}
              onCommit={doc.commitStylePreview}
              onCancel={doc.cancelStylePreview}
            />
          </div>
        </>
      ) : null}
      {control === 'width' && bondIds.length > 0 ? (
        <div className="selection-width-presets">
          {[undefined, 0.5, 1, 1.5, 2, 3].map((value) => (
            <button
              type="button"
              key={value ?? 'standard'}
              aria-pressed={width !== MIXED && width === value}
              onClick={() => apply({ lineWidth: value })}
            >
              {value === undefined ? t('rightRail.style.standard') : `${value} ${t('rightRail.style.pt')}`}
            </button>
          ))}
        </div>
      ) : null}
      {control !== 'color' && bondIds.length > 0 ? (
        <label className="style-row">
          <span>{t('rightRail.style.bondWidth')}</span>
          <span className="style-number">
            <input
              type="text"
              inputMode="decimal"
              aria-label={t('rightRail.style.bondWidthPt')}
              placeholder={width === MIXED ? mixed : t('rightRail.style.standard')}
              value={widthText ?? (width === MIXED || width === undefined ? '' : String(width))}
              onChange={(event) => setWidthText(event.target.value)}
              onBlur={commitWidth}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commitWidth();
                if (event.key === 'Escape') setWidthText(null);
              }}
            />
            <span>{t('rightRail.style.pt')}</span>
          </span>
        </label>
      ) : null}
      {control === 'all' && atomIds.length > 0 ? (
        <div
          className="arrange-relative style-radio-row"
          role="radiogroup"
          aria-label={t('rightRail.style.atomColours')}
        >
          <span>{t('rightRail.style.atomColours')}</span>
          {(['monochrome', 'byElement'] as const).map((mode) => {
            const active = byElement !== MIXED && (mode === 'byElement') === (byElement === true);
            return (
              <label key={mode} className={active ? 'active' : undefined}>
                <input
                  type="radio"
                  name="molecule-atom-colours"
                  checked={active}
                  onChange={() => apply({ colorByElement: mode === 'byElement' ? true : undefined })}
                />
                {t(`rightRail.style.atomColour.${mode}`)}
              </label>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function ObjectStylePanel({
  doc,
  bondConfig,
  onColorChange,
  onNumericChange,
  defaultsStatus,
  showIonicBonds = false,
  onToggleShowIonicBonds,
  showHapticity = false,
  onToggleShowHapticity
}: {
  readonly doc: UseDrawingDocumentResult;
  readonly bondConfig: BondDrawingConfig;
  readonly onColorChange: (color: string) => void;
  readonly onNumericChange: (key: keyof BondDrawingConfig, value: string, minimum: number) => void;
  readonly defaultsStatus?: StyleDefaultsSyncStatus | undefined;
  // ADR 0022 (as amended) -- a view option for this session, like Show R/S Labels: never saved,
  // exported, printed or copied.
  readonly showIonicBonds?: boolean;
  readonly onToggleShowIonicBonds?: (value: boolean) => void;
  // ADR 0026 D2 -- η labels on hapto bonds, a view option for this session in the same way.
  readonly showHapticity?: boolean;
  readonly onToggleShowHapticity?: (value: boolean) => void;
}) {
  const { t } = useTranslation();
  // #868 -- atoms and bonds in the selection (a whole molecule or a segment of one).
  const moleculeTargets = appearanceTargets(doc.document, doc.selection);
  const hasMolecule = moleculeTargets.atomIds.length > 0 || moleculeTargets.bondIds.length > 0;
  const selected = doc.document.annotations
    .filter((annotation) => doc.selection.annotationIds?.includes(annotation.id))
    .filter(isStyledAnnotation);
  const canStyle = selected.length > 0 || hasMolecule;
  const { commitStylePreview } = doc;
  // A pending preview is committed when the selection changes or the tab closes.
  const selectionKey = [
    ...selected.map((annotation) => annotation.id),
    ...moleculeTargets.atomIds,
    ...moleculeTargets.bondIds
  ].join(',');
  useEffect(() => () => commitStylePreview(), [commitStylePreview, selectionKey]);

  const keys: readonly AnnotationStyleKey[] = [
    'color',
    'lineStyle',
    'fill',
    'lineWidth',
    'opacity',
    'cornerRadius',
    'shadow',
    'outline',
    'background'
  ];
  const selectionValues = Object.fromEntries(keys.map((key) => [key, shared(selected, key)])) as {
    [K in AnnotationStyleKey]: Value<AnnotationStyle[K]>;
  };
  const defaults = doc.annotationStyleDefaults;

  return (
    <>
      {hasMolecule ? (
        <section className="rail-section" aria-label={t('rightRail.style.molecule')}>
          <h2>{t('rightRail.style.molecule')}</h2>
          <p className="rail-hint">{t('rightRail.style.moleculeHint')}</p>
          <MoleculeControls doc={doc} />
        </section>
      ) : null}
      <section className="rail-section" aria-label={t('rightRail.selectionStyle')}>
        <h2>{t('rightRail.selectionStyle')}</h2>
        <p className="rail-hint">
          {selected.length ? t('rightRail.styledCount', { count: selected.length }) : t('rightRail.selectStyle')}
        </p>
        {selected.length ? (
          <StyleControls
            idPrefix="selection"
            values={selectionValues}
            accepts={(key) => selected.some((annotation) => applicableStyleKeys(annotation).includes(key))}
            onPreview={doc.previewAnnotationStyle}
            onCommit={commitStylePreview}
            onCancel={doc.cancelStylePreview}
          />
        ) : null}
        {/* #861 (#842 S8) -- style actions; ⌥⌘C / ⌥⌘V (Alt+Ctrl on Windows). */}
        <div className="style-actions">
          <button
            type="button"
            className="icon-button"
            aria-label={t('rightRail.style.copyStyle')}
            title={canStyle ? t('rightRail.style.copyStyleHint') : t('rightRail.style.copyStyleDisabled')}
            aria-disabled={!canStyle || undefined}
            onClick={() => {
              if (canStyle) doc.copyStyle();
            }}
          >
            <Icon name="copy" />
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label={t('rightRail.style.pasteStyle')}
            title={
              !doc.hasCopiedStyle
                ? t('rightRail.style.pasteStyleNothing')
                : canStyle
                  ? t('rightRail.style.pasteStyleHint')
                  : t('rightRail.style.pasteStyleDisabled')
            }
            aria-disabled={!doc.hasCopiedStyle || !canStyle || undefined}
            onClick={() => {
              if (doc.hasCopiedStyle && canStyle) doc.pasteStyle();
            }}
          >
            <Icon name="paste" />
          </button>
        </div>
      </section>
      <section className="rail-section" aria-label={t('rightRail.newObjects')}>
        <h2>{t('rightRail.newObjects')}</h2>
        <p className="rail-hint">{t('rightRail.defaultStyleHint')}</p>
        {/* Only the failure note: a "Saving…" line came and went with every save and moved the tab. */}
        {defaultsStatus === 'unsaved' ? (
          <output className="rail-hint style-defaults-status" data-testid="style-defaults-status">
            {t('rightRail.style.defaultsStatus.unsaved')}
          </output>
        ) : null}
        {/* #862 (#842 S9) -- Set as default copies the selected object's style; Reset restores the standard look. */}
        <div className="style-actions">
          <button
            type="button"
            className="icon-button"
            aria-label={t('rightRail.style.setDefault')}
            title={selected.length ? t('rightRail.style.setDefaultHint') : t('rightRail.style.setDefaultDisabled')}
            aria-disabled={selected.length === 0 || undefined}
            onClick={() => {
              if (selected.length) doc.setAnnotationStyleDefaults({ ...STANDARD_DEFAULTS, ...selected[0]!.style });
            }}
          >
            <Icon name="star" />
          </button>
          <button
            type="button"
            className="icon-button"
            aria-label={t('rightRail.style.resetDefaults')}
            title={t('rightRail.style.resetDefaultsHint')}
            onClick={() => doc.setAnnotationStyleDefaults(STANDARD_DEFAULTS)}
          >
            <Icon name="refresh" />
          </button>
        </div>
        <StyleControls
          idPrefix="defaults"
          values={{
            color: defaults.color,
            lineStyle: defaults.lineStyle,
            fill: defaults.fill,
            lineWidth: defaults.lineWidth,
            opacity: defaults.opacity,
            cornerRadius: defaults.cornerRadius,
            shadow: defaults.shadow,
            outline: defaults.outline,
            background: defaults.background
          }}
          accepts={() => true}
          onPreview={(patch) =>
            doc.setAnnotationStyleDefaults((current) => {
              const next: Record<string, unknown> = { ...current };
              for (const [key, value] of Object.entries(patch)) {
                if (value === undefined) delete next[key];
                else next[key] = value;
              }
              return next as AnnotationStyle;
            })
          }
          onCommit={() => undefined}
          onCancel={() => undefined}
        />
      </section>
      <section className="rail-section">
        <h2>{t('rightRail.bonds')}</h2>
        <p className="rail-hint">{t('rightRail.bondStyleHint')}</p>
        {/* ADR 0022 (as amended) -- every bolt between a salt's ions, on the paper only. */}
        <label className="style-check">
          <input
            type="checkbox"
            checked={showIonicBonds}
            onChange={(event) => onToggleShowIonicBonds?.(event.target.checked)}
          />
          <Icon name="bolt" />
          <span>{t('rightRail.style.showIonicBonds')}</span>
        </label>
        <label className="style-check">
          <input
            type="checkbox"
            checked={showHapticity}
            onChange={(event) => onToggleShowHapticity?.(event.target.checked)}
          />
          <BondGlyph variant="hapto" />
          <span>{t('rightRail.style.showHapticity')}</span>
        </label>
        <BondSettingsPanel
          bondConfig={bondConfig}
          onColorChange={onColorChange}
          onNumericChange={onNumericChange}
          colorPalette={
            <ChemicalPalettePicker value={bondConfig.color ?? DEFAULT_INK_COLOR} onSelect={onColorChange} />
          }
        />
      </section>
    </>
  );
}
